import React, { useState } from 'react';
import { useWindowSize } from 'react-use';

import Axios from 'axios';

import styled from 'styled-components';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadPlaylist, forceReloadPlaylistInfo, usePlaylistInfo } from '@hooks/API/UsePlaylist';
import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

import Comments from '@components/Comments/Comments';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import MediaPackages from '@components/MediaDrawer/MediaPackages';
import MediaRatings from '@components/MediaDrawer/MediaRatings';
import MediaWebsites from '@components/MediaDrawer/MediaWebsites';
import PlaylistDetails from '@components/MediaDrawer/PlaylistDetails';
import ObjectTagsCard from '@components/ObjectTagsCard/ObjectTagsCard';

const CardLine = styled.div`
    margin-top: 1rem;
`;

const DrawerColumns = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1rem;
    align-items: start;

    @media only screen and (max-width: 1199px) {
        grid-template-columns: 1fr;
    }
`;

const DrawerColumn = styled.div`
    min-width: 0;

    ${CardLine}:first-child {
        margin-top: 0;
    }
`;

const PlayListInfoDrawer = ({
    onPublishBook,
    playlist,
    contactID,
    readOnly,
    onClose,
    isEdit,
    onDeletePlaylists,
    onUpdatePlaylists,
}) => {
    const [touchForm, setTouchForm] = useState(false);
    const { width: windowWidth } = useWindowSize();
    const adjustTagUsageCount = useAdjustTagUsageCount();

    const { data: playlistData } = usePlaylistInfo(playlist.ID);
    const isCompactDrawer = windowWidth < 1200;

    const handleDeletePlayList = () => {
        onDeletePlaylists(playlistData);
        onClose();
    };

    const handleAddTag = async (tag) => {
        const tagData = {
            idp: playlistData.ID,
            idt: tag.ID,
        };

        try {
            await Axios.post(`/playlists/addtag/${playlistData.ID}/${tag.ID}`, tagData);
            adjustTagUsageCount(tag.ID, 1);
            onUpdatePlaylists();
            forceReloadPlaylistInfo(playlistData.ID);
            forceReloadPlaylist(playlistData.ID);
        } catch (error) {
            console.error('errore', error);
        }
    };

    const handleRemoveTag = async (tag) => {
        const tagData = {
            playlistID: playlist.ID,
            tagID: tag.ID,
        };

        try {
            await Axios.post(`/playlists/removetag/${playlistData.ID}/${tag.ID}`, tagData);
            adjustTagUsageCount(tag.ID, -1);
            onUpdatePlaylists();
            forceReloadPlaylistInfo(playlistData.ID);
            forceReloadPlaylist(playlistData.ID);
        } catch (error) {
            //TODO: manage error on api call, may be by adding an ErrorMessage to the state and a condition to show it in render.
            console.error('errore', error);
        }
    };

    const handleMediaUpdate = () => {
        onUpdatePlaylists();
    };

    return (
        <NwDrawer
            touchForm={touchForm}
            onTouchForm={setTouchForm}
            onClose={onClose}
            icon={readOnly && 'times'}
            whiteBg={false}
            bgColor={readOnly ? `rgba(0, 0, 0, .5)` : null}
            width={isCompactDrawer ? '40rem' : '80rem'}
            title={`${getContactName(playlist.ContactID || contactID)} / ${playlist.Name}`}
        >
            {/* Il gate e' sui soli dati, non su isFetching: quello e' vero anche durante i
                refetch in background (es. dopo aver aggiunto un tag) e faceva sparire tutto il
                drawer dietro <Loading />. React Query tiene i dati precedenti finche' arrivano
                i nuovi, quindi il contenuto resta a video e si aggiorna sul posto, come fa
                MediaDrawer con il suo stato locale. */}
            {playlistData ? (
                <DrawerColumns>
                    <DrawerColumn>
                        <CardLine>
                            <PlaylistDetails
                                isEdit={isEdit}
                                playlist={playlistData}
                                onTouchForm={setTouchForm}
                                onDeletePlayList={handleDeletePlayList}
                                onUpdatePlaylists={() => {
                                    forceReloadPlaylistInfo(playlistData.ID);
                                    forceReloadPlaylist(playlistData.ID);
                                    onUpdatePlaylists();
                                }}
                            />
                        </CardLine>
                        <CardLine>
                            <MediaWebsites book={playlistData} onPublishBook={onPublishBook} />
                        </CardLine>
                        <CardLine>
                            <MediaPackages playlistID={playlistData.ID} />
                        </CardLine>
                    </DrawerColumn>
                    <DrawerColumn>
                        <CardLine>
                            <ObjectTagsCard
                                objectType={'Playlist'}
                                vertical
                                objectTags={playlistData.Tags}
                                onAddTag={handleAddTag}
                                onRemoveTag={handleRemoveTag}
                            />
                        </CardLine>
                        <CardLine>
                            <MediaRatings
                                objectType={'Playlist'}
                                vertical
                                objectId={playlistData.ID}
                                onMediaUpdate={handleMediaUpdate}
                            />
                        </CardLine>
                        <CardLine>
                            <Comments
                                api="PlaylistID"
                                comments={playlistData.Comments}
                                ID={playlistData.ID}
                                disableVisibility
                                drawerMode
                                onTouchForm={setTouchForm}
                                onUpdate={(comment) => {
                                    forceReloadPlaylist(playlistData.ID);
                                    forceReloadPlaylistInfo(playlistData.ID);
                                    onUpdatePlaylists();
                                }}
                                isPaginated={true}
                            />
                        </CardLine>
                    </DrawerColumn>
                </DrawerColumns>
            ) : (
                <Loading />
            )}
        </NwDrawer>
    );
};

export default PlayListInfoDrawer;
